import React, {useEffect, useMemo, useRef, useState} from 'react';
import styles from "./Participation.module.scss";
import LoadingSpinner from "../../Components/LoadingSpinner/LoadingSpinner";
import {DataGrid} from "@mui/x-data-grid";
import moment from "moment";
import TextInput from "../../Components/TextInput/TextInput";
import {hasValue} from "../../helpers/helperFunctions";
import ViewParticipationModal, {SALE_TYPE_IDO} from "./ViewParticipationModal";
import Button, {
  BUTTON_TYPE_PRIMARY,
  BUTTON_TYPE_PRIMARY_BRIGHT,
  BUTTON_TYPE_SECONDARY
} from "../../Components/Button/Button";
import {PROD_API_URL} from "../../constants/constants";
import {generateMerkle} from "../../helpers/merkleHelper";
import {getNodeSaleEndUnix, whitelistMerkleRootToPool} from "../../helpers/idoHelper";
import toast from "react-hot-toast";
import {errorToast, informationToast, successToast} from "../../helpers/toastCSS";


const columns = [

  { field: 'wallet', headerName: 'Wallet', flex: 1},
  { field: 'username', headerName: 'Username', flex: 0.5},
  { field: 'contributedStarsAmount', headerName: 'Contributed Stars', flex: 0.5 },
  { field: 'userAllocation', headerName: 'Allocation', flex: 0.5 },
  { field: 'contributedUSDCAmount', headerName: 'Contributed USDC', flex: 0.5 },
  { field: 'transactionsAmount', headerName: 'No. of Transactions', flex: 0.5  },
  { field: 'lastTransactionTime', headerName: 'Last Transaction Time',  flex: 0.5, maxWidth: '200px'  },
];

const Participation = ({ ido, provider }) => {

  const [rows, setRows] = useState();
  const [loading, setLoading] = useState(true);
  const [selectedData, setSelectedData] = useState();

  const [openGenerateMerkleModal, setOpenGenerateMerkleModal] = useState(false);
  const [merkleJSON, setMerkleJSON] = useState();

  const [importParticipantsModal, setImportParticipantsModal] = useState(false);
  const [participantsJSON, setParticipantsJSON] = useState();
  const [allocationJSON, setAllocationJSON] = useState();
  const [allocationPerHydrazine, setAllocationPerHydrazine] = useState(1);

  const [totalStats, setTotalStats] = useState({ stars: 0, usdc: 0 });
  const [filterName, setFilterName] = useState();
  const [maxAllocation, setMaxAllocation] = useState();

  useEffect( ()=>{
    const fetchPossibleParticipations = async () => {
      try {
        await fetch(PROD_API_URL+"/participation/" + ido.id, {
          "method": "GET",
          "headers": {"content-type": "application/json", "accept": "*"},
        }).then(res=>res.json())
            .then((result)=>{
              if (result.data) {
                handleResult(result);
              } else {
                throw new Error(result);
              }
            });
      } catch (e) {
        console.error(e);
        toast('Error fetching participations: ' + e, errorToast);
      } finally {
        setLoading(false);
      }
    }

    if (ido.id) {
      fetchPossibleParticipations();
    }
  },[ido]);

  const handleResult = (result) => {
    let totalStars = 0;
    let totalUSDC = 0;
    const mappedData = result?.data.map(item => {
      totalStars = totalStars + Number(item.contributedStarsAmount);
      totalUSDC = totalUSDC + Number(item.contributedUSDCAmount);
      return {
        id: item.solanaWallet,
        username: item.username,
        wallet: item.solanaWallet,
        transactionsAmount: item.transactionsAmount,
        transactions: item.transactions,
        lastTransactionTime: moment.unix(item.lastTransactionTime / 1000).format('YYYY-MM-DD , h:mm A'),
        userAllocation: item.userAllocation ? Number(item.userAllocation).toFixed(2) : '',
        contributedStarsAmount: item?.contributedStarsAmount ? Number(item?.contributedStarsAmount).toFixed(2) : '',
        contributedUSDCAmount: item?.contributedUSDCAmount ? Number(item?.contributedUSDCAmount).toFixed(2) : '',
        purchasedTickets: item.purchasedTickets,
      }
    })
    setRows(mappedData);
    setTotalStats({ stars: totalStars.toFixed(2), usdc: totalUSDC.toFixed(2) });
  }

  const mappedColumns = useMemo(() => {
    return columns.filter(column => !hasValue(column.saleType));
  }, [ido]);

  const filteredRows = useMemo(() => {
    return rows?.filter(row => {
      if (hasValue(filterName) && !row?.name.toLowerCase().includes(filterName.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [rows, filterName]);

  const saveAllocations = async (merkleResult) => {
    const data = {
      poolName: ido.poolname,
      merkleRoot: merkleResult.fileData.merkleRoot.toString("hex"),
      tokenTotal: merkleResult.tokenTotal,
      numNodes: merkleResult.numNodes,
      claims: merkleResult.fileData.claims,
    }
    await fetch(PROD_API_URL+"/saveAllocations/", {
      "method": "POST",
      "headers": {"content-type": "application/json", "accept": "*"},
      "body": JSON.stringify(data)
    });
  }

  const downloadJson = (newJson) => {
    const blob = new Blob([newJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'generated_merkle_' + ido.poolName + '.json';  // default filename
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleMerkle = async () => {
    try {
      const result = generateMerkle(merkleJSON)

      await whitelistMerkleRootToPool({ provider, poolRoot: result.root, poolName: ido.poolname });
      await saveAllocations(result);
      downloadJson(JSON.stringify(result.fileData));
      setOpenGenerateMerkleModal(false);
      toast('Merkle Tree Generated!', successToast);
    } catch (error) {
      console.error(error);
      toast(`Error generating merkle: ${error.message}`, errorToast);
    }
  }

  const loadJSON = async (e, setState) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        setState(JSON.parse(reader.result));
      } catch(err) {
        console.error('Invalid JSON', err);
      }
    };
    reader.readAsText(file);
  }

  const saveParticipants = async () => {
    toast('Saving ' + participantsJSON.length + ' participants', informationToast);
    for (let i = 0; i < participantsJSON.length; i++) {
      try {
        const transaction = {
          timestamp: ido.isNodeSale ? getNodeSaleEndUnix(ido) * 1000 : ido.salesStartTime * 1000,
        }

        const participant = participantsJSON[i];
        let userAllocation;
        let userStarsContribution;

        if (hasValue(participant["NodeAmount"])) {
          transaction.nodeAmount = participant["NodeAmount"];
          transaction.nodePrice = participant["USDC"] / participant["NodeAmount"];
        } else {
          if (allocationJSON) {
            const allocationsEntries = Object.entries(allocationJSON["claims"]);
            const allocationObject = allocationsEntries.find(allocation => allocation[0] === participant["Address"]);
            if (allocationObject) {
              const actualAllocation = Number(allocationObject[1].amount) / 1000000;
              userAllocation = actualAllocation;
              userStarsContribution = actualAllocation / Number(allocationPerHydrazine);
              transaction.starsAmount = actualAllocation / Number(allocationPerHydrazine);
            }

            transaction.usdcAmount = Number(participant["USDC"]);
          } else {
            transaction.usdcAmount = Number(participant["USDC"]);
          }
        }

        const userTransactions = [transaction];

        const body = {
          idoId: ido.id,
          solanaWallet: participant["Address"],
          idoParticipation: {
            idoName: ido?.name,
            idoId: ido.id,
            isNodeSale: ido.isNodeSale,
            transactions: userTransactions,
          },
        };

        if (hasValue(userAllocation)) {
          body.idoParticipation.userAllocation = userAllocation;
        }

        if (ido.isNodeSale) {
          body.idoParticipation.purchasedNodeAmount = participant["NodeAmount"];
          body.idoParticipation.nodePrice = participant["USDC"] / participant["NodeAmount"];
        } else {
          body.idoParticipation.contributedStarsAmount = userStarsContribution;
          body.idoParticipation.contributedUSDCAmount = participant["USDC"];
        }

        await fetch(PROD_API_URL+"/ido/saveIdoTransaction", {
          "method": "POST",
          "headers": {"content-type": "application/json", "accept": "*"},
          "body": JSON.stringify(body)
        });
      } catch (e) {
        console.error(e);
        toast('Error saving participant: ' + e, errorToast);
      }
    }

    toast('Participants Saved!', successToast);
    setImportParticipantsModal(false);
  }

  const updateParticipants = async () => {
    toast('Saving ' + participantsJSON.length + ' participants', informationToast);
    for (let i = 0; i < participantsJSON.length; i++) {
      try {
        const participant = participantsJSON[i];

        let userAllocation;
        let userStarsContribution;

        if (allocationJSON) {
          const allocationsEntries = Object.entries(allocationJSON["claims"]);
          const allocationObject = allocationsEntries.find(allocation => allocation[0] === participant["Address"]);
          if (allocationObject) {
            const actualAllocation = Number(allocationObject[1].amount) / 1000000;
            userAllocation = actualAllocation;
            userStarsContribution = actualAllocation / Number(allocationPerHydrazine);
          }

        }

        const body = {
          //valueKey: 'contributedStarsAmount',
          //transactionKey: 'starsAmount',
         // valueValue: userStarsContribution,
         // userAllocation,
          timestamp: ido.isNodeSale ? getNodeSaleEndUnix(ido) * 1000 : ido.salesStartTime * 1000,
          idoId: ido.id,
          solanaWallet: participant["Address"],
        }

        await fetch('http://localhost:1337'+ "/cadetParticipation/update", {
          "method": "POST",
          "headers": {"content-type": "application/json", "accept": "*"},
          "body": JSON.stringify(body)
        });
      } catch (e) {
        console.error(e);
        toast('Error saving participant: ' + e, errorToast);
      }
    }

    toast('Participants Saved!', successToast);
    setImportParticipantsModal(false);
  }

  const fetchUserAllocations = () => {
    const tokenPrice = 1 / ido.swaprate;
    const totalRaise = ido.poolcap * tokenPrice

    const allocations = rows.map(row => {
      const allocation = (totalRaise / totalStats.stars) *  Number(row.contributedStarsAmount);

      return {
        wallet: row.wallet,
        amount: Math.round(allocation * 1000000),
      }
    })
    setMerkleJSON(allocations);
  }

  return (
      <div className={styles.mainParticipationContainer}>
        <div className={styles.idoAdminContainer}>
          <div className={styles.participationFilterContainer}>
            <div className={styles.participationFilterTextInput}>
              <TextInput label="Filter By Name" value={filterName} setValue={setFilterName}/>
            </div>
            <Button buttonType={BUTTON_TYPE_SECONDARY} text="Import Participants"
                    onClick={() => setImportParticipantsModal(true)}/>
            <Button buttonType={BUTTON_TYPE_SECONDARY} text="Generate Merkle"
                    onClick={() => setOpenGenerateMerkleModal(true)}/>
          </div>
          <div className={styles.idoTableContainer}>
            {loading && <LoadingSpinner local/>}
            {!loading &&
                <DataGrid
                    rows={filteredRows}
                    columns={mappedColumns}
                    onCellDoubleClick={(params) => setSelectedData(params.row)}
                />}
          </div>
        </div>
        {importParticipantsModal && <div className={styles.deleteIdoModalOverlay}>
          <div className={styles.deleteIdoModal}>
            <div className={styles.generateAllocationContent}>
              <span>Import participant JSON</span>
              <br/>
              <span>Either upload a JSON file of users wallet and amount, or press the button below to fetch it from database</span>
              <div className={styles.maxAllocationContainer}>
                <TextInput label="Input Allocation Per Hydrazine" value={allocationPerHydrazine} setValue={setAllocationPerHydrazine}/>
              </div>
              <br/>
              <div className={styles.deleteRow}>
                <span>Import Participants</span>
                <input type="file" accept=".json" onChange={(e) => loadJSON(e, setParticipantsJSON)}/>
              </div>
              <div className={styles.deleteRow}>
                <span>Import Allocations</span>
                <input type="file" accept=".json" onChange={(e) => loadJSON(e, setAllocationJSON)}/>
              </div>
              <div className={styles.jsonContainer}>
                {participantsJSON && <pre>{JSON.stringify(participantsJSON, null, 2)}</pre>}
              </div>
            </div>
            <div className={styles.deleteRow}>
              <Button buttonType={BUTTON_TYPE_PRIMARY_BRIGHT} text="CANCEL"
                      onClick={() => setImportParticipantsModal(false)}/>
              <Button
                  buttonType={BUTTON_TYPE_SECONDARY}
                  text="UPDATE"
                  disabled={!hasValue(allocationPerHydrazine)}
                  onClick={updateParticipants}/>
              <Button
                    buttonType={BUTTON_TYPE_SECONDARY}
                    text="SAVE"
                    disabled={!hasValue(allocationPerHydrazine)}
                    onClick={saveParticipants}/>
              </div>
            </div>
          </div>
          }
          {openGenerateMerkleModal && <div className={styles.deleteIdoModalOverlay}>
          <div className={styles.deleteIdoModal}>
            <div className={styles.generateAllocationContent}>
              <span>Generate Allocations for Participants by creating a Merkle Tree</span>
              <br/>
              <span>TOTAL STARS CONTRIBUTED: {totalStats.stars}</span>
              <br />
              <span>Either upload a JSON file of users wallet and amount, or press the button below to fetch it from database</span>
              <div className={styles.deleteRow}>
                <div className={styles.maxAllocationContainer}>
                  <TextInput label="Input Max Allocation in Percent" value={maxAllocation} setValue={setMaxAllocation}/>
                </div>
                <Button buttonType={BUTTON_TYPE_SECONDARY} text="Fetch from Database" onClick={fetchUserAllocations}/>
              </div>
              <br/>
              <input type="file" accept=".json" onChange={(e) => loadJSON(e, setMerkleJSON)}/>
              <div className={styles.jsonContainer}>
                {merkleJSON && <pre>{JSON.stringify(merkleJSON, null, 2)}</pre>}
              </div>
            </div>
            <div className={styles.deleteRow}>
              <Button buttonType={BUTTON_TYPE_PRIMARY_BRIGHT} text="CANCEL"
                      onClick={() => setOpenGenerateMerkleModal(false)}/>
              <Button
                  buttonType={BUTTON_TYPE_SECONDARY}
                  text="GENERATE"
                  onClick={handleMerkle}/>
            </div>
          </div>
        </div>}
        {selectedData && <ViewParticipationModal
            participationData={selectedData}
            setParticipationData={setSelectedData}
            saleName={ido?.name}
            saleType={SALE_TYPE_IDO}/>}
      </div>);
}

export default Participation;
